import React, { useEffect, useState } from 'react';
import { Icon } from './ui';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  message: string;
  type: ToastType;
  duration?: number;
  onClose: () => void;
}

const STYLES: Record<ToastType, {
  bg: string; text: string; iconBg: string; iconColor: string; icon: string; bar: string;
}> = {
  success: {
    bg: 'bg-white',
    text: 'text-slate-900',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    icon: 'check_circle',
    bar: 'bg-emerald-500',
  },
  error: {
    bg: 'bg-white',
    text: 'text-slate-900',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    icon: 'error',
    bar: 'bg-red-500',
  },
  warning: {
    bg: 'bg-white',
    text: 'text-slate-900',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    icon: 'warning',
    bar: 'bg-amber-500',
  },
  info: {
    bg: 'bg-white',
    text: 'text-slate-900',
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    icon: 'info',
    bar: 'bg-primary',
  },
};

const Toast: React.FC<ToastProps> = ({ message, type, duration = 3500, onClose }) => {
  const [isExiting, setIsExiting] = useState(false);
  const s = STYLES[type] || STYLES.info;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(onClose, 200);
    }, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return (
    <div
      className={[
        s.bg, s.text,
        'relative flex items-start gap-3 p-3 pe-4 ps-3 min-w-[280px] max-w-sm',
        'rounded-xl border border-slate-100 shadow-xl shadow-slate-900/10',
        'overflow-hidden',
        isExiting ? 'animate-[toast-out_200ms_ease-in_forwards]' : 'animate-[toast-in_250ms_cubic-bezier(0.16,1,0.3,1)]',
      ].join(' ')}
      role="alert"
    >
      {/* Colored side bar */}
      <span className={`absolute top-0 bottom-0 start-0 w-1 ${s.bar}`} />

      {/* Icon */}
      <div className={`${s.iconBg} ${s.iconColor} size-9 rounded-lg flex items-center justify-center flex-shrink-0 ms-1`}>
        <Icon name={s.icon} size={20} />
      </div>

      {/* Message */}
      <div className="flex-1 min-w-0 py-1.5">
        <div className="text-sm font-semibold leading-tight">{message}</div>
      </div>

      {/* Close button */}
      <button
        onClick={() => { setIsExiting(true); setTimeout(onClose, 200); }}
        className="size-7 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 flex items-center justify-center flex-shrink-0 transition-colors"
        aria-label="סגור"
      >
        <Icon name="close" size={16} />
      </button>

      <style>{`
        @keyframes toast-in {
          from { transform: translateX(-16px); opacity: 0; }
          to   { transform: translateX(0); opacity: 1; }
        }
        @keyframes toast-out {
          to { transform: translateX(-16px); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default Toast;
